// Popup variant of the legacy `PromptInModal` (JobPrompt.tsx:160-177) for use on
// the flag-on /WarrantyAdd/0 page route. We do not export PromptInModal from
// JobPrompt.tsx because it requires a modalConfig.beforeClose; here we own the
// close handler ourselves (history-aware navigation).
//
// If the legacy popup's visual treatment is changed, mirror it here.
import { useEffect, useState } from "react";
import { type RouteComponentProps } from "react-router";

import { type BTSelectItem } from "types/apiResponse/apiResponse";

import { showAPIErrorMessage } from "utilities/apiHandler";
import { routes } from "utilities/routes";

import { BTModal } from "commonComponents/btWrappers/BTModal/BTModal";
import { DependentEntitiesPrompt } from "commonComponents/helpers/DependentEntitiesPrompt/DependentEntitiesPrompt";
import { DependentEntity } from "commonComponents/helpers/DependentEntitiesPrompt/DependentEntitiesPrompt.api.types";
import {
    type IJobPromptHandler,
    JobPromptHandler,
} from "commonComponents/helpers/JobPrompt/JobPrompt.handler";
import { BTLoading } from "commonComponents/utilities/BTLoading/BTLoading";

interface IProps extends Pick<RouteComponentProps, "history" | "location"> {
    jobPromptHandler?: IJobPromptHandler;
}

// Hoisted to a module constant so the default prop has a stable identity across
// renders. Otherwise eslint flags the `new` expression default and the useEffect
// dep array would re-fire on every render.
const defaultJobPromptHandler: IJobPromptHandler = new JobPromptHandler();

export const WarrantyAddJobPickerModal = ({
    history,
    location,
    jobPromptHandler = defaultJobPromptHandler,
}: IProps) => {
    const [jobs, setJobs] = useState<BTSelectItem[] | undefined>(undefined);

    useEffect(() => {
        let cancelled = false;
        void jobPromptHandler
            .get()
            .then((response) => {
                if (!cancelled) setJobs(response.jobs);
            })
            .catch((e: unknown) => {
                // A failed GetExistingJobList fetch (or a malformed response that
                // throws while constructing ExistingJobListResponse) would otherwise
                // become an unhandled rejection and strand the user on BTLoading —
                // see Sentry BTNET-REACT-BFN2 / BTNET-REACT-B7WJ. Surface the error
                // and close the picker back to the warranty list.
                if (!cancelled) {
                    showAPIErrorMessage(e);
                    history.replace(routes.warranty.getListLink());
                }
            });
        return () => {
            cancelled = true;
        };
    }, [jobPromptHandler, history]);

    // Same deep-link signal used elsewhere on this branch (Warranty.tsx handleClose):
    // history v4 + react-router v5 leave location.key undefined on the initial
    // browser entry. TODO(history-migration): location.key becomes always-truthy
    // in history v6 — replace both call sites with an SPA-navigated flag.
    const handleClose = () => {
        const isDeepLink = history.action === "POP" && !location.key;
        if (isDeepLink) {
            history.replace(routes.warranty.getListLink());
        } else {
            history.goBack();
        }
    };

    const handleSelect = (jobId: number) => {
        // Preserve location.state so callers can pass intent (e.g., the warranty
        // QuickAdd interstitial's `redirectToWarrantyListAfterSave`) through the
        // job-prompt step. Without this, the post-save redirect logic in
        // Warranty.tsx loses the opt-in flag and falls back to `goBack`.
        history.replace(routes.warranty.getAddPageLink(jobId), location.state);
    };

    if (!jobs) return <BTLoading />;

    // Hardcoded "Job" — warranties don't use templates. PromptInModal derives this
    // from the API's isTemplateMode flag (JobPrompt.tsx:161,126); we elide that
    // branch because warranty's JobPromptHandler response is always job-mode.
    return (
        <BTModal
            data-testid="btModalJobPrompt"
            title="Select a Job"
            setPageTitle={false}
            visible
            centered
            width="400px"
            beforeClose={handleClose}
            overflow="visible"
            removeBodyPadding
        >
            <DependentEntitiesPrompt
                dependencies={[new DependentEntity("Job", handleSelect, jobs)]}
            />
        </BTModal>
    );
};
